import { config } from '../config';

export interface MarketSearchResult {
  uuid: string;
  marketUuid: string;
  divisionUuid: string;
  name: string;
  cashpool: number;
  minApr: number;
  minDpe: number;
  desiredApr: number;
  nextPayDate: string;
  lastPayDay: string;
  nextClearTime: string;
  isEnabled: boolean;
  currency: string;
  marketId: number;
  marketType: string | null;
}

interface ApiMarketListItem {
  divisionId: number;
  marketId: number;
  marketUuid: string;
  divisionUuid: string;
  divisionName?: string;
  name?: string;
  currency: string;
  marketType: string | null;
  cashpool: number | null;
  minApr: number | null;
  minDpe: number;
  desiredApr: number | null;
  nextPayDate: string | null;
  lastPayDay: string | null;
  nextClearTime: string;
  isEnabled: boolean;
}

function toSearchResult(apiItem: ApiMarketListItem): MarketSearchResult {
  return {
    uuid: apiItem.marketUuid,
    marketUuid: apiItem.marketUuid,
    divisionUuid: apiItem.divisionUuid,
    name: apiItem.divisionName || apiItem.name || 'Unknown',
    cashpool: apiItem.cashpool || 0,
    minApr: apiItem.minApr || 0,
    minDpe: apiItem.minDpe,
    desiredApr: apiItem.desiredApr || 0,
    nextPayDate: apiItem.nextPayDate || '',
    lastPayDay: apiItem.lastPayDay || '',
    nextClearTime: apiItem.nextClearTime,
    isEnabled: apiItem.isEnabled,
    currency: apiItem.currency,
    marketId: apiItem.marketId,
    marketType: apiItem.marketType,
  };
}

/**
 * Fuzzy string matching for market search
 * @param text Text to search in
 * @param pattern Search pattern
 * @returns true if pattern matches
 */
function fuzzyMatch(text: string | null | undefined, pattern: string): boolean {
  if (!text) return false;

  const textLower = text.toLowerCase();
  const patternLower = pattern.toLowerCase();

  // Direct substring match
  if (textLower.includes(patternLower)) {
    return true;
  }

  // Check if all words in pattern are in the text
  const words = patternLower.split(/\s+/);
  return words.every((word) => textLower.includes(word));
}

/**
 * Calculate relevance score for sorting search results
 */
function calculateRelevanceScore(market: MarketSearchResult, query: string): number {
  const queryLower = query.toLowerCase();
  let score = 0;

  // Exact match on UUID gets highest score
  if (market.marketUuid.toLowerCase() === queryLower || market.divisionUuid.toLowerCase() === queryLower) {
    score += 100;
  }

  // Name match is next highest priority
  const nameLower = market.name.toLowerCase();
  if (nameLower === queryLower) {
    score += 50;
  } else if (nameLower.startsWith(queryLower)) {
    score += 30;
  } else if (nameLower.includes(queryLower)) {
    score += 20;
  }

  // Currency match is lower priority
  if (market.currency.toLowerCase() === queryLower) {
    score += 10;
  }

  // Enabled markets get a small boost
  if (market.isEnabled) {
    score += 5;
  }

  return score;
}

export async function searchMarkets(query: string, token: string, signal?: AbortSignal): Promise<MarketSearchResult[]> {
  const response = await fetch(`${config.ADMIN_API_URL}/api/admin/v2/market/all`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText} (${response.status})`);
  }

  const apiData: { markets: ApiMarketListItem[] } = await response.json();

  if (!Array.isArray(apiData.markets)) {
    throw new Error('Invalid response format from API');
  }

  const allMarkets = apiData.markets.map(toSearchResult);

  if (!query || query.trim() === '') {
    return allMarkets;
  }

  const filtered = allMarkets.filter(
    (market) =>
      fuzzyMatch(market.name, query) ||
      fuzzyMatch(market.marketUuid, query) ||
      fuzzyMatch(market.divisionUuid, query) ||
      fuzzyMatch(market.currency, query),
  );

  filtered.sort((a, b) => {
    const scoreA = calculateRelevanceScore(a, query);
    const scoreB = calculateRelevanceScore(b, query);
    return scoreB - scoreA;
  });

  return filtered;
}
